import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import {
  HiOutlineCircleStack,
  HiOutlineCodeBracket,
  HiOutlineKey,
  HiOutlinePuzzlePiece,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { APP_ROUTES, IResponseError } from "../../data-access";
import { useUser } from "../../hooks";
import { canUseCrm } from "../../util/plan-gates";
import { FormButton, Panel } from "../../ui";
import { ToolCard } from "./tool-card";
import {
  getDevExtensionId,
  saveDevExtensionId,
} from "../../util/get-extension-id";
import { billingApi, toolsApi } from "../../api";
import { ByokKeyModal } from "../byok-key";
import { handleFailedRequest, notify } from "../../util";
import { storeToolsActions } from "../../store/slices/tools";
import { RootState } from "../../store";
import "./tools-and-apps.scss";

export const ToolsAndApps = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useUser();

  const isPipedriveConnected = useSelector(
    (state: RootState) => state.tools.is_pipedrive_connected
  );

  const [authUrl, setAuthUrl] = useState("");

  useEffect(() => {
    toolsApi
      .getPipedriveStatus()
      .then((response) => {
        const isConnected = response.data.is_connected;
        dispatch(
          storeToolsActions.setPipedriveConnection({ is_connected: isConnected })
        );
        if (!isConnected) {
          return toolsApi.getPipedriveAuthUrl();
        }
      })
      .then((authResponse) => {
        if (authResponse) {
          setAuthUrl(authResponse.data.url);
        }
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      });
  }, [dispatch]);

  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const handlePipedriveClick = () => {
    if (isPipedriveConnected) {
      navigate(APP_ROUTES.pipedriveSettings);
    } else if (authUrl) {
      window.location.href = authUrl;
    }
  };

  const handlePipedriveDisconnect = () => {
    setIsDisconnecting(true);
    toolsApi
      .deletePipedrive()
      .then(() => {
        notify("success", "Pipedrive has been successfully disconnected.");
        dispatch(
          storeToolsActions.setPipedriveConnection({ is_connected: false })
        );
        toolsApi
          .getPipedriveAuthUrl()
          .then((response: { data: { url: string } }) => {
            setAuthUrl(response.data.url);
          });
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsDisconnecting(false));
  };

  // OpenRouter key (Flex plan BYOK). Seeded from /api/user, then kept in
  // sync from the byok-key endpoint responses.
  const [byok, setByok] = useState(currentUser?.byok ?? null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showKeyRemoveConfirm, setShowKeyRemoveConfirm] = useState(false);
  const [isRemovingKey, setIsRemovingKey] = useState(false);

  useEffect(() => {
    if (currentUser?.byok) setByok(currentUser.byok);
  }, [currentUser?.byok]);

  const handleRemoveKey = () => {
    setIsRemovingKey(true);
    billingApi
      .deleteByokKey()
      .then((response) => {
        setByok((prev) => ({ ...(prev ?? { enabled: true }), ...response, enabled: true }));
        setShowKeyRemoveConfirm(false);
        notify("success", "OpenRouter key removed. AI runs on your plan credits again.");
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text);
      })
      .finally(() => setIsRemovingKey(false));
  };

  const byokStatusBadge = () => {
    if (!byok?.has_key) return null;
    if (byok.status === "invalid")
      return <span className="tools-and-apps__warn-badge tools-and-apps__warn-badge--error">Key invalid</span>;
    if (byok.status === "no_credits")
      return <span className="tools-and-apps__warn-badge">No balance</span>;
    // Green dot already says "connected"; the word itself pushed Replace
    // onto a second row on 4-column layouts.
    return (
      <span className="tools-and-apps__connected-badge" title="Connected">
        ****{byok.key_last4}
      </span>
    );
  };

  const isAvailableAPI = currentUser && currentUser?.paid_user;

  // Dev Extension ID
  const [devExtId, setDevExtId] = useState("");
  const [devSaved, setDevSaved] = useState(false);
  const devSavedTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (process.env.REACT_APP_DEV_MODE === "true") {
      setDevExtId(
        getDevExtensionId() || process.env.REACT_APP_EXTENSION_ID || ""
      );
    }
  }, []);

  const handleSaveDevExtId = () => {
    saveDevExtensionId(devExtId);
    setDevSaved(true);
    if (devSavedTimer.current) clearTimeout(devSavedTimer.current);
    devSavedTimer.current = setTimeout(() => setDevSaved(false), 2000);
  };

  return (
    <Panel title="Tools & Apps" className="tools-and-apps">
      <div className="tools-and-apps__cards">
        <ToolCard
            title="Pipedrive"
            icon={<HiOutlineCircleStack size={22} />}
            statusComponent={
              isPipedriveConnected ? (
                <>
                  <span className="tools-and-apps__connected-badge">
                    Connected
                  </span>
                  <button
                    className="tools-and-apps__disconnect-btn"
                    onClick={() => setShowDisconnectConfirm(true)}
                    disabled={isDisconnecting}
                  >
                    <HiOutlineTrash size={16} />
                  </button>
                </>
              ) : null
            }
            footerComponent={
              canUseCrm(currentUser?.plan) ? (
                <FormButton
                  theme="green-light"
                  onClick={handlePipedriveClick}
                  disabled={!isPipedriveConnected && !authUrl}
                >
                  {isPipedriveConnected ? "Configure" : "Connect"}
                </FormButton>
              ) : (
                <span className="tools-and-apps__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )
            }
          >
            Syncs data from Upwork to Pipedrive CRM, providing an easy
            integration between the platforms.
          </ToolCard>

          {byok?.enabled && (
            <ToolCard
              title="OpenRouter AI Key"
              icon={<HiOutlineKey size={22} />}
              statusComponent={
                byok?.has_key ? (
                  <>
                    {byokStatusBadge()}
                    <button
                      className="tools-and-apps__disconnect-btn"
                      onClick={() => setShowKeyRemoveConfirm(true)}
                      disabled={isRemovingKey}
                    >
                      <HiOutlineTrash size={16} />
                    </button>
                  </>
                ) : null
              }
              footerComponent={
                <FormButton
                  theme="green-light"
                  onClick={() => {
                    // Otherwise antd hands focus back on close and the button
                    // keeps a focus ring.
                    (document.activeElement as HTMLElement | null)?.blur();
                    setShowKeyModal(true);
                  }}
                >
                  {byok?.has_key ? "Update" : "Connect"}
                </FormButton>
              }
            >
              Run AI generation on your own OpenRouter balance - no credit
              limits. Your 50 monthly plan credits stay as a backup.
            </ToolCard>
          )}

          {process.env.REACT_APP_DEV_MODE === "true" && (
            <ToolCard
              title="Upwex API"
              icon={<HiOutlineCodeBracket size={22} />}
              iconColor="blue"
              footerComponent={
                <FormButton
                  theme="green-light"
                  onClick={() => navigate(APP_ROUTES.api)}
                  disabled={!isAvailableAPI}
                >
                  Manage Keys
                </FormButton>
              }
            >
              Seamlessly integrate Upwex's API & Webhooks into your platform.
              {!isAvailableAPI && (
                <span className="tools-and-apps__plan-note">
                  Available for paid plans.
                </span>
              )}
            </ToolCard>
          )}
          {process.env.REACT_APP_DEV_MODE === "true" && (
            <ToolCard
              title="Extension ID"
              icon={<HiOutlinePuzzlePiece size={22} />}
              iconColor="blue"
              footerComponent={
                <div className="tools-and-apps__dev-row">
                  <input
                    className="tools-and-apps__dev-input"
                    value={devExtId}
                    onChange={(e) => setDevExtId(e.target.value)}
                    placeholder="Extension ID"
                  />
                  <FormButton theme="green-light" onClick={handleSaveDevExtId}>
                    {devSaved ? "Saved!" : "Save"}
                  </FormButton>
                </div>
              }
            >
              Override Chrome extension ID for local development. Saved to localStorage.
            </ToolCard>
          )}
      </div>

      {/* Disconnect Pipedrive confirmation */}
      <Modal
        open={showDisconnectConfirm}
        className="disconnect-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={() => setShowDisconnectConfirm(false)}
        footer={null}
        title={null}
        width={380}
      >
        <div className="disconnect-modal__content">
          <div className="disconnect-modal__icon">
            <HiOutlineTrash size={24} />
          </div>
          <h3 className="disconnect-modal__title">Disconnect Pipedrive</h3>
          <p className="disconnect-modal__desc">
            Are you sure you want to disconnect Pipedrive? Your CRM settings will be lost.
          </p>
          <div className="disconnect-modal__buttons">
            <FormButton
              theme="grey"
              onClick={() => setShowDisconnectConfirm(false)}
            >
              Cancel
            </FormButton>
            <FormButton
              theme="red"
              onClick={() => {
                setShowDisconnectConfirm(false);
                handlePipedriveDisconnect();
              }}
              loading={isDisconnecting}
            >
              Disconnect
            </FormButton>
          </div>
        </div>
      </Modal>

      {/* Connect / replace OpenRouter key */}
      <ByokKeyModal
        open={showKeyModal}
        onClose={() => setShowKeyModal(false)}
        onSaved={(state) => setByok((prev) => ({ ...(prev ?? { enabled: true }), ...state, enabled: true }))}
      />

      {/* Remove OpenRouter key confirmation */}
      <Modal
        open={showKeyRemoveConfirm}
        className="disconnect-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={() => setShowKeyRemoveConfirm(false)}
        footer={null}
        title={null}
        width={380}
      >
        <div className="disconnect-modal__content">
          <div className="disconnect-modal__icon">
            <HiOutlineTrash size={24} />
          </div>
          <h3 className="disconnect-modal__title">Remove OpenRouter key</h3>
          <p className="disconnect-modal__desc">
            AI generation will switch back to your plan credits (50 / month).
            You can reconnect the key anytime.
          </p>
          <div className="disconnect-modal__buttons">
            <FormButton theme="grey" onClick={() => setShowKeyRemoveConfirm(false)}>
              Cancel
            </FormButton>
            <FormButton theme="red" onClick={handleRemoveKey} loading={isRemovingKey}>
              Remove
            </FormButton>
          </div>
        </div>
      </Modal>
    </Panel>
  );
};

export default ToolsAndApps;
